import { useEffect } from 'react';

export interface ApiComparisonResult {
  key: string;
  apiA: { label: string; durationMs: number };
  apiB: { label: string; durationMs: number };
  /** apiA.durationMs − apiB.durationMs (positive ⇒ B was faster) */
  differenceMs: number;
}

interface UseApiComparisonParams {
  /** Unique key to namespace performance marks across hook instances */
  key: string;
  enabled: boolean;
  apiA: { label: string; isFetching: boolean };
  apiB: { label: string; isFetching: boolean };
  /** Called once per fetch cycle when both APIs have responded */
  onCompare?: (result: ApiComparisonResult) => void;
}

/**
 * Compares response times of two parallel API queries using
 * `performance.mark/measure`. Fires the `onCompare` callback exactly once
 * per fetch cycle when both queries complete.
 *
 * Global performance marks deduplicate across multiple component instances
 * that consume the same hook.
 */
export function useApiComparison({
  key,
  enabled,
  apiA,
  apiB,
  onCompare,
}: UseApiComparisonParams) {
  const markA = `${key}:a-start`;
  const markB = `${key}:b-start`;
  const measureA = `${key}:a-duration`;
  const measureB = `${key}:b-duration`;

  const measure = (mark: string, measureName: string) => {
    if (performance.getEntriesByName(mark, 'mark').length > 0) {
      performance.measure(measureName, mark);
      performance.clearMarks(mark);
    }
  };

  const tryCompare = () => {
    const entriesA = performance.getEntriesByName(measureA, 'measure');
    const entriesB = performance.getEntriesByName(measureB, 'measure');
    if (entriesA.length === 0 || entriesB.length === 0) return;

    const durA = entriesA[0].duration;
    const durB = entriesB[0].duration;

    performance.clearMeasures(measureA);
    performance.clearMeasures(measureB);

    onCompare?.({
      key,
      apiA: { label: apiA.label, durationMs: Math.round(durA) },
      apiB: { label: apiB.label, durationMs: Math.round(durB) },
      differenceMs: Math.round(durA - durB),
    });
  };

  useEffect(() => {
    if (enabled && apiA.isFetching) {
      performance.clearMarks(markA);
      performance.clearMeasures(measureA);
      performance.mark(markA);
    }
  }, [enabled, apiA.isFetching, markA, measureA]);

  useEffect(() => {
    if (enabled && apiB.isFetching) {
      performance.clearMarks(markB);
      performance.clearMeasures(measureB);
      performance.mark(markB);
    }
  }, [enabled, apiB.isFetching, markB, measureB]);

  return {
    onApiASuccess: enabled
      ? () => {
          measure(markA, measureA);
          tryCompare();
        }
      : undefined,
    onApiBSuccess: enabled
      ? () => {
          measure(markB, measureB);
          tryCompare();
        }
      : undefined,
  };
}
